import { Field, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { LuEye, LuEyeOff, LuLoaderCircle } from 'react-icons/lu'
import { Link } from 'wouter'
import * as Yup from 'yup'

export default function ResetPasswordForm({
  showGuide,
  error,
  requestedResetPassword,
  handleSubmit
}) {
  // Estados para mostrar/ocultar contraseña
  const [seePassword, setSeePassword] = useState(false)
  const [seePasswordConfirmation, setSeePasswordConfirmation] = useState(false)

  // Esquema de validación del formulario
  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required('Introduzca una contraseña')
      .oneOf(
        [Yup.ref('password_confirmation')],
        'Las contraseñas deben coincidir'
      )
      .min(6, 'La contraseña es demasiado corta. Mínimo 6 caracteres'),
    password_confirmation: Yup.string()
      .required('Introduzca una contraseña')
      .oneOf([Yup.ref('password')], 'Las contraseñas deben coincidir')
      .min(6, 'La contraseña es demasiado corta. Mínimo 6 caracteres')
  })

  return (
    <>
      {!showGuide ? (
        <Formik
          initialValues={{
            password: ''
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSubmit(values)
          }}
        >
          {({ errors, touched }) => (
            <Form className="px-4 py-3">
              <h3 className="mt-5 text-xl font-medium text-center text-gray-600 dark:text-gray-200">
                Restablezca su contraseña
              </h3>

              <p className="mt-1 text-center text-gray-500 dark:text-gray-400">
                Cree una nueva contraseña para recuperar el acceso
              </p>

              {/* Contraseña */}
              <div className="mt-8">
                <label
                  title="Contraseña"
                  htmlFor="password"
                  className={`${touched.password && errors.password ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                >
                  Contraseña
                </label>

                <div className="relative flex items-center">
                  <button
                    type="button"
                    className="absolute right-0 -translate-y-2 focus:outline-none rtl:left-0 rtl:right-auto top-1/2"
                    onClick={() => setSeePassword((prevState) => !prevState)}
                    tabIndex="-1"
                  >
                    {seePassword ? (
                      <LuEyeOff
                        title="Esconder contraseña"
                        className={`w-6 h-6 mx-4 transition-colors duration-300 ${touched.password && errors.password ? 'text-red-900 dark:text-red-500 hover:text-red-700 dark:hover:text-red-500' : ' text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400'}`}
                      />
                    ) : (
                      <LuEye
                        title="Mostrar contraseña"
                        className={`w-6 h-6 mx-4 transition-colors duration-300 ${touched.password && errors.password ? 'text-red-900  dark:text-red-500 hover:text-red-700 dark:hover:text-red-500' : ' text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400'}`}
                      />
                    )}
                  </button>

                  <Field
                    id="password"
                    name="password"
                    type={seePassword ? 'text' : 'password'}
                    placeholder={'********'}
                    className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.password && errors.password ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                    autoComplete="new-password"
                  />
                </div>

                <div
                  className={`flex justify-start items-start w-full ${touched.password && errors.password ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600">
                      {errors.password || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Repetir contraseña */}
              <div className="mt-4">
                <label
                  title="Repetir contraseña"
                  className={`${touched.password_confirmation && errors.password_confirmation ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="password_confirmation"
                >
                  Repetir contraseña
                </label>

                <div className="relative flex items-center">
                  <button
                    type="button"
                    className="absolute right-0 -translate-y-2 focus:outline-none rtl:left-0 rtl:right-auto top-1/2"
                    onClick={() =>
                      setSeePasswordConfirmation((prevState) => !prevState)
                    }
                    tabIndex="-1"
                  >
                    {seePasswordConfirmation ? (
                      <LuEyeOff
                        title="Esconder contraseña"
                        className={`w-6 h-6 mx-4 transition-colors duration-300 ${touched.password_confirmation && errors.password_confirmation ? 'text-red-900 dark:text-red-500 hover:text-red-700 dark:hover:text-red-500' : ' text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400'}`}
                      />
                    ) : (
                      <LuEye
                        title="Mostrar contraseña"
                        className={`w-6 h-6 mx-4 transition-colors duration-300 ${touched.password_confirmation && errors.password_confirmation ? 'text-red-900  dark:text-red-500 hover:text-red-700 dark:hover:text-red-500' : ' text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400'}`}
                      />
                    )}
                  </button>

                  <Field
                    id="password_confirmation"
                    name="password_confirmation"
                    type={seePasswordConfirmation ? 'text' : 'password'}
                    placeholder={'********'}
                    className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.password_confirmation && errors.password_confirmation ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                    autoComplete="new-password"
                  />
                </div>

                <div
                  className={`flex justify-start items-start w-full ${touched.password_confirmation && errors.password_confirmation ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600">
                      {errors.password_confirmation || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center w-full mt-5 space-y-4">
                <button
                  title={
                    requestedResetPassword.loading
                      ? 'Restableciendo'
                      : 'Restablecer'
                  }
                  type="submit"
                  className="flex items-center justify-center w-full px-5 py-2 text-white capitalize transition-colors duration-200 border rounded-sm bg-accent border-accent gap-x-2 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
                  disabled={requestedResetPassword.loading}
                >
                  <LuLoaderCircle
                    className={`animate-spin ${requestedResetPassword.loading ? 'block' : 'hidden'}`}
                    size={24}
                  />
                  {requestedResetPassword.loading
                    ? 'Restableciendo'
                    : 'Restablecer'}
                </button>

                <Link
                  title="Cancelar"
                  href={`/login`}
                  className="text-sm text-gray-400 focus:text-blue-500 hover:text-blue-500 hover:underline"
                >
                  Cancelar
                </Link>
              </div>

              <div
                className={`relative text-sm px-2 py-1 mt-2 text-red-600 bg-red-100 border border-red-600 rounded-sm ${error ? 'visible' : 'invisible'}`}
                role="alert"
              >
                <strong className="font-bold">Error: </strong>
                <span className="block sm:inline">{error}</span>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="px-4 pt-3 pb-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-800 dark:text-white">
              Contraseña restablecida exitosamente
            </h1>

            <div className="mt-2 text-gray-600 dark:text-gray-300">
              <span>Su contraseña ha sido restablecida correctamente. </span>

              <span>
                Por favor, intente acceder a su cuenta utilizando sus nuevas
                credenciales.
              </span>
            </div>
          </div>

          <div className="flex items-center justify-center mt-8">
            <Link
              href={`/login`}
              className="flex items-center justify-center w-full px-5 py-2 text-white capitalize transition-colors duration-200 border rounded-sm bg-accent border-accent gap-x-2 hover:bg-white hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
            >
              Aceptar
            </Link>
          </div>
        </div>
      )}
    </>
  )
}

ResetPasswordForm.propTypes = {
  showGuide: PropTypes.bool,
  error: PropTypes.any,
  requestedResetPassword: PropTypes.any,
  handleSubmit: PropTypes.func
}
